import {
  buildRefFromCoords,
  insertRefIntoBuffer,
} from "@library-elements/table";
import { useAppDispatch, useAppSelector } from "@store";
import { updateTableFormulaEdit } from "@store/story";
import { getTableFormulaEdit } from "@store/story/selectors";
import { type MutableRefObject, useEffect, useRef } from "react";

interface IProps {
  entityId: string;
  tableWrapperRef: MutableRefObject<HTMLDivElement | null>;
}

// Ref-picking during in-cell formula edit: capture-phase pointer handlers that
// insert/extend A1 refs into the formula buffer as the author clicks or drags
// across cells, and suppress the trailing click / dnd-kit drag / context menu.
export const useFormulaPick = ({ entityId, tableWrapperRef }: IProps) => {
  const dispatch = useAppDispatch();

  // Refs hold live values for the capture-phase pointer handlers. pointerdown
  // snapshots the buffer; each pointermove rebuilds from it so drag-extend
  // never compounds.
  const formulaEdit = useAppSelector(getTableFormulaEdit);
  const formulaEditRef = useRef(formulaEdit);
  formulaEditRef.current = formulaEdit;
  const pickStateRef = useRef<{
    anchor: { col: number; row: number };
    isAdditive: boolean;
    bufferAtPickStart: string;
  } | null>(null);
  // rAF-coalesced pointermove → Redux dispatch (pointermove fires ~120/s).
  const pendingMoveRef = useRef<{ col: number; row: number } | null>(null);
  const moveRafRef = useRef<number | null>(null);
  // One-shot flag: pointerup sets it, click capture consumes it on the next
  // click to suppress the bubble-phase click following the pointer sequence.
  const suppressNextClickRef = useRef(false);

  useEffect(() => {
    // Pointer events + document-level capture so dnd-kit's PointerSensor and
    // element-attached listeners run after us. preventDefault on pointerdown
    // also cancels the synthesized mousedown.
    const cellHitFromPoint = (
      clientX: number,
      clientY: number,
    ): { col: number; row: number; cellId: string } | null => {
      const el = document.elementFromPoint(clientX, clientY);
      if (!(el instanceof HTMLElement)) return null;
      const cellEl = el.closest("[data-cell-id]");
      if (!(cellEl instanceof HTMLElement)) return null;
      if (!tableWrapperRef.current?.contains(cellEl)) return null;
      const cellId = cellEl.getAttribute("data-cell-id");
      if (!cellId) return null;
      const col = Number(cellEl.getAttribute("data-cell-col"));
      const row = Number(cellEl.getAttribute("data-cell-row"));
      if (!Number.isFinite(col) || !Number.isFinite(row)) return null;
      return { col, row, cellId };
    };

    const handlePointerDownCapture = (e: PointerEvent) => {
      const fEdit = formulaEditRef.current;
      if (!fEdit) return;
      // Pick only inserts refs for the table owning the active formula edit
      // (cell ids repeat across tables).
      if (fEdit.entityId !== entityId) return;
      // Primary button only — except macOS Ctrl+click (button=2), treated as an
      // append pick. The contextmenu event is suppressed separately below.
      if (e.button !== 0 && !(e.button === 2 && e.ctrlKey)) return;
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      const cellEl = target.closest("[data-cell-id]");
      if (!(cellEl instanceof HTMLElement)) return;
      if (!tableWrapperRef.current?.contains(cellEl)) return;
      const cellId = cellEl.getAttribute("data-cell-id");
      // Clicking inside the editing cell — let the input own caret/selection.
      if (!cellId || cellId === fEdit.cellId) return;

      // Skip pick when the press lands on cell chrome (resize handles,
      // sort/filter/merge buttons) so those keep working during formula edit.
      if (
        target.closest(
          "[data-is-col-resize], [data-is-row-resize], [data-table-cell-actions], button",
        )
      ) {
        return;
      }

      const col = Number(cellEl.getAttribute("data-cell-col"));
      const row = Number(cellEl.getAttribute("data-cell-row"));
      if (!Number.isFinite(col) || !Number.isFinite(row)) return;

      // Suppress everything else on this gesture: dnd-kit's PointerSensor, the
      // synthesized mousedown, and any browser text selection.
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      const isAdditive = e.metaKey || e.ctrlKey;
      pickStateRef.current = {
        anchor: { col, row },
        isAdditive,
        bufferAtPickStart: fEdit.buffer,
      };

      // Commit the initial single-cell ref; drag-extend overwrites it.
      const newRef = buildRefFromCoords({ col, row }, { col, row });
      const newBuffer = insertRefIntoBuffer(
        fEdit.buffer,
        newRef,
        isAdditive ? "append" : "replace",
      );
      dispatch(updateTableFormulaEdit(newBuffer));
    };

    const handlePointerMoveCapture = (e: PointerEvent) => {
      const pick = pickStateRef.current;
      if (!pick) return;
      // Hit-test via elementFromPoint (e.target stays glued to the start cell
      // during a fast drag due to pointer capture).
      const hit = cellHitFromPoint(e.clientX, e.clientY);
      if (!hit) return;
      pendingMoveRef.current = { col: hit.col, row: hit.row };

      if (moveRafRef.current !== null) return;
      moveRafRef.current = requestAnimationFrame(() => {
        moveRafRef.current = null;
        const pending = pendingMoveRef.current;
        const state = pickStateRef.current;
        if (!pending || !state) return;
        const newRef = buildRefFromCoords(state.anchor, pending);
        const newBuffer = insertRefIntoBuffer(
          state.bufferAtPickStart,
          newRef,
          state.isAdditive ? "append" : "replace",
        );
        dispatch(updateTableFormulaEdit(newBuffer));
      });
    };

    const handlePointerUpCapture = (e: PointerEvent) => {
      const state = pickStateRef.current;
      if (!state) return;
      // Commit the final drag endpoint synchronously: a quick drag/release can
      // leave the last cell queued in a rAF that the cancel below would drop,
      // truncating the picked range.
      const hit = cellHitFromPoint(e.clientX, e.clientY);
      const finalCell = hit
        ? { col: hit.col, row: hit.row }
        : pendingMoveRef.current;
      if (moveRafRef.current !== null) {
        cancelAnimationFrame(moveRafRef.current);
        moveRafRef.current = null;
      }
      if (finalCell) {
        const newRef = buildRefFromCoords(state.anchor, finalCell);
        const newBuffer = insertRefIntoBuffer(
          state.bufferAtPickStart,
          newRef,
          state.isAdditive ? "append" : "replace",
        );
        dispatch(updateTableFormulaEdit(newBuffer));
      }
      pickStateRef.current = null;
      pendingMoveRef.current = null;
      // Bridge to click-capture: suppress the bubble-phase click that follows
      // so it can't re-enter the regular cell-selection path.
      suppressNextClickRef.current = true;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };

    // Blocks the bubble-phase click that follows a pick gesture so it can't
    // reach the regular cell-selection handler. Gated by the one-shot flag set
    // during pointerup; a normal click (flag unset) passes through.
    const handleClickCapture = (e: MouseEvent) => {
      if (!suppressNextClickRef.current) return;
      suppressNextClickRef.current = false;
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (!tableWrapperRef.current?.contains(target)) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };

    // Suppress the right-click menu mid formula-edit, and on a macOS Ctrl+click
    // additive-pick (which fires contextmenu). A plain right-click still opens it.
    const handleContextMenuCapture = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      if (!tableWrapperRef.current?.contains(target)) return;
      const isCtrlPickOnCell =
        !!formulaEditRef.current &&
        e.ctrlKey &&
        !!target.closest("[data-cell-id]");
      if (!formulaEditRef.current && !isCtrlPickOnCell) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    };

    document.addEventListener(
      "pointerdown",
      handlePointerDownCapture as EventListener,
      true,
    );
    document.addEventListener("contextmenu", handleContextMenuCapture, true);
    document.addEventListener(
      "pointermove",
      handlePointerMoveCapture as EventListener,
      true,
    );
    document.addEventListener(
      "pointerup",
      handlePointerUpCapture as EventListener,
      true,
    );
    document.addEventListener("click", handleClickCapture, true);

    return () => {
      document.removeEventListener(
        "pointerdown",
        handlePointerDownCapture as EventListener,
        true,
      );
      document.removeEventListener(
        "pointermove",
        handlePointerMoveCapture as EventListener,
        true,
      );
      document.removeEventListener(
        "pointerup",
        handlePointerUpCapture as EventListener,
        true,
      );
      document.removeEventListener("click", handleClickCapture, true);
      document.removeEventListener(
        "contextmenu",
        handleContextMenuCapture,
        true,
      );
      if (moveRafRef.current !== null) {
        cancelAnimationFrame(moveRafRef.current);
        moveRafRef.current = null;
      }
    };
  }, [entityId]);
};
